import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, X, Loader2 } from 'lucide-react';

interface SearchableSelectProps {
    value: any;
    onChange: (val: any) => void;
    onSearch: (query: string) => Promise<any[]>;
    getLabel: (item: any) => string;
    getValue: (item: any) => any;
    placeholder?: string;
    disabled?: boolean;
    minChars?: number;
    defaultOptions?: any[];
    /** Optional two-line row in the dropdown list */
    renderOption?: (item: any) => React.ReactNode;
    getOptionKey?: (item: any) => string;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
    value, onChange, onSearch, getLabel, getValue, placeholder = 'Zoeken...', disabled = false, minChars = 0, defaultOptions = [],
    renderOption, getOptionKey,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState<any[]>(defaultOptions);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<any>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && minChars === 0 && options.length === 0 && !query) {
            fetchOptions('');
        }
    }, [isOpen]);

    const fetchOptions = async (q: string) => {
        if (q.length < minChars) {
            setOptions(defaultOptions);
            return;
        }
        setLoading(true);
        try {
            const results = await onSearch(q);
            setOptions(results);
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setQuery(val);
        setIsOpen(true);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchOptions(val);
        }, 300);
    };

    const handleSelect = (option: any) => {
        onChange(getValue(option));
        setQuery('');
        setIsOpen(false);
    };

    const displayValue = value ? getLabel(value) : '';

    return (
        <div ref={wrapperRef} className="relative w-full">
            <div 
                className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] font-medium bg-white flex items-center justify-between cursor-pointer ${disabled ? 'opacity-50 pointer-events-none' : 'hover:border-blue-400'}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <div className="truncate text-slate-800">{displayValue || <span className="text-slate-300">{placeholder}</span>}</div>
                <div className="flex items-center gap-1">
                    {value && !disabled && (
                        <X size={14} className="text-slate-400 hover:text-red-500" onClick={(e) => { e.stopPropagation(); onChange(null); }} />
                    )}
                    <ChevronDown size={14} className="text-slate-400" />
                </div>
            </div>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 flex flex-col">
                    <div className="p-2 border-b border-slate-100 flex items-center gap-2">
                        <Search size={14} className="text-slate-400 shrink-0" />
                        <input
                            type="text"
                            value={query}
                            onChange={handleInput}
                            placeholder={minChars > 0 ? `Typ minimaal ${minChars} letters om te zoeken...` : 'Zoeken...'}
                            className="w-full text-[12px] outline-none placeholder:text-slate-300"
                            autoFocus
                        />
                        {loading && <Loader2 size={14} className="text-blue-500 animate-spin shrink-0" />}
                    </div>
                    <div className="overflow-y-auto p-1">
                        {options.length === 0 && !loading ? (
                            <div className="p-3 text-center text-[11px] text-slate-400">Geen resultaten gevonden</div>
                        ) : (
                            options.map((opt, i) => (
                                <div
                                    key={getOptionKey ? getOptionKey(opt) : (opt?.id ?? i)}
                                    onClick={() => handleSelect(opt)}
                                    className={`px-3 py-2 hover:bg-blue-50 cursor-pointer rounded text-[12px] text-slate-700 ${renderOption ? '' : 'truncate'}`}
                                >
                                    {renderOption ? renderOption(opt) : getLabel(opt)}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
